// MIDAS V3 — Response card (Phase 2)
//
// Ports `PinolaCard` structure from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (lines 265-358)
//
// Card structure (spec §5):
//   1. Title row (padding 14×18, bottom border): 22×22 accent-soft tile + ⚡
//      glyph + bold 14.5 title. No right-side count.
//   2. Body — children passthrough. v3 reskins, doesn't rewrite, the markdown
//      rendering that v2 already does. ChatBotMessageV3 will mount its
//      markdown output here.
//   3. Warning callout (optional) — amber-soft callout for "open service
//      report on this subsystem". Used when the v2 bot SSE produced an
//      `srCitations` entry that resolves to an open SR.
//   4. Follow-ups tray (optional) — `var(--card-2)` bg + "Would you also
//      like" eyebrow + accent-bordered pill chips. Maps from v2 message
//      `suggestions` field.
//
// Wrap-no-restyle invariant: this card is a CHROME. The markdown content
// passed as children renders with its own v3-themed table/list/code/cite
// styling (handled by ChatBotMessageV3). The card does not introspect or
// re-style the children.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ResponseSourceCardV3 from "./ResponseSourceCardV3";

export interface ResponseCardWarning {
  /** Bold first line — e.g. "Open service report on this subsystem". */
  title: string;
  /** Mono subline — e.g. "SR-260326-001 · Nefunguje Pinola · opened 26.03.2026". */
  subtitle?: string;
  /** Button label (defaults to "Open ↗"). */
  buttonLabel?: string;
  /** Click handler for the right-aligned button. */
  onButtonClick?: () => void;
}

export interface ResponseCardFollowUp {
  /** Chip label. */
  label: string;
  /** Click handler (typically resends as a follow-up suggestion). */
  onClick?: () => void;
}

/**
 * A single entry in the consolidated "Sources" footer.
 *
 * The footer renders below the body and any warning, and above the follow-ups
 * tray. Each row is keyboard-focusable: clicking it dispatches the same
 * artifact-open flow as an inline `<CiteV3>` chip.
 *
 * 2026-05-18 follow-up to MIDAS-V3-P2: user expected a consolidated source
 * list under the response (mirrors the bot's prose "Zdroje:" convention),
 * not just the inline citation pills.
 */
export interface ResponseCardSource {
  /** Mono pill text — e.g. "Sheet 020". Required, drives the badge in the
   *  source card's text strip. */
  label: string;
  /** Page number (canonical join key). Passed through to onClick verbatim. */
  page: number;
  /** Localised name shown below the pill — e.g. "DC 24V/10A Power Supply".
   *  Optional. */
  name?: string;
  /** Relative bot-image proxy path (e.g. "/api/v1/image/<doc>/...page_047.jpg")
   *  populated by ChatBotMessageV3 from the matching `message.sources[i]`.
   *  When undefined, the card renders without a thumbnail well. */
  imageUrl?: string;
  /** Click handler. Receives the entry's `page`. */
  onClick?: (page: number) => void;
}

export interface ResponseCardV3Props {
  /** Title text — e.g. "Quill / Tailstock (Pinola) — Connections". */
  title?: string;
  /** Main body (markdown rendered by ChatBotMessageV3). */
  children: React.ReactNode;
  /** Optional amber warning callout shown between body and follow-ups. */
  warning?: ResponseCardWarning;
  /** Optional consolidated sources footer — renders BELOW body+warning, ABOVE
   *  follow-ups. Each row pairs a mono `List NNN` chip with the localised
   *  page name. Pass undefined or [] to hide the section. */
  sources?: ResponseCardSource[];
  /** Optional follow-up chip tray rendered at the bottom of the card. */
  followUps?: ResponseCardFollowUp[];
}

const ResponseCardV3: React.FC<ResponseCardV3Props> = ({
  title,
  children,
  warning,
  sources,
  followUps,
}) => {
  const { translate } = useTranslation("chatBot");
  const hasFollowUps = !!followUps && followUps.length > 0;
  const hasWarning = !!warning;
  const hasTitle = !!title;
  const hasSources = !!sources && sources.length > 0;

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "var(--shadow)",
        fontFamily: "var(--font-sans)",
        color: "var(--ink)",
      }}
    >
      {hasTitle && (
        // R3 sub-task 4a: dropped the 22×22 accent-soft tile + ⚡ glyph from
        // the title row. Title text alone is the cleaner header. No callers
        // pass `titleIcon` (verified by grep across src/), so the prop was
        // removed from the public surface as well.
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--border-soft)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14.5,
              color: "var(--ink)",
              letterSpacing: "-0.005em",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        </div>
      )}

      {/* Body — passthrough region. v3 markdown renderer mounts here. */}
      <div
        style={{
          padding: "14px 18px",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
        }}
      >
        {children}
      </div>

      {hasSources && (
        // MIDAS-V3-BUGS-0519 sub-task 5 — port v2's source-cards strip.
        //
        // The previous quiet mono-text rendering ("List 007 — Sheet 020 -
        // DC 24V/10A Power Supply" stacked rows) reads as plain log output,
        // not as actionable references. v2 renders sources as small card
        // thumbnails with a preview image + label badge + page name —
        // makes the references feel like first-class chips you can click.
        //
        // We render a horizontal flex row with `flexWrap: wrap` so 1-source
        // responses stay compact and 5+-source responses wrap to a second
        // line cleanly. Top padding only — bottom keeps the existing 14px
        // so the spacing under the cards lines up with the card's prose.
        <div
          style={{
            padding: "4px 18px 16px",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "stretch",
          }}
        >
          {sources!.map((src, i) => (
            <ResponseSourceCardV3
              key={`${src.page}-${i}`}
              label={src.label}
              page={src.page}
              name={src.name}
              imageUrl={src.imageUrl}
              onClick={src.onClick}
            />
          ))}
        </div>
      )}

      {hasWarning && (
        <div
          style={{
            margin: hasFollowUps ? "0 18px 14px" : "0 18px 14px",
            padding: "10px 12px",
            // The handoff uses color-mix for the warning fill. We approximate
            // with a fixed-opacity rgba pulled from --amber so it works in
            // browsers without OKLAB color-mix support. The visual effect is
            // the same warm-yellow tint on top of the card.
            background:
              "color-mix(in oklab, var(--amber) 12%, var(--card))",
            border:
              "1px solid color-mix(in oklab, var(--amber) 38%, var(--border))",
            borderRadius: 8,
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontSize: 13,
          }}
        >
          <span
            aria-hidden="true"
            style={{ color: "var(--amber)", flexShrink: 0, fontSize: 14 }}
          >
            ⚠
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "var(--ink)" }}>
              {warning.title}
            </div>
            {warning.subtitle && (
              <div
                className="mono"
                style={{
                  color: "var(--muted)",
                  fontSize: 12,
                  marginTop: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {warning.subtitle}
              </div>
            )}
          </div>
          {warning.onButtonClick && (
            <button
              type="button"
              onClick={warning.onButtonClick}
              style={{
                height: 26,
                padding: "0 10px",
                fontSize: 12,
                fontFamily: "var(--font-sans)",
                background: "var(--card)",
                color: "var(--ink-2)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {warning.buttonLabel ??
                (translate("midas.v3.responseCard.openSrButton", {
                  defaultValue: "Open ↗",
                }) as string)}
            </button>
          )}
        </div>
      )}

      {hasFollowUps && (
        <div
          style={{
            padding: "10px 14px",
            borderTop: "1px solid var(--border-soft)",
            background: "var(--card-2)",
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.1em",
              color: "var(--faint)",
              textTransform: "uppercase",
              marginRight: 4,
            }}
          >
            {translate("midas.v3.responseCard.followUpsEyebrow", {
              defaultValue: "Would you also like",
            }) as string}
          </span>
          {followUps!.map((chip, i) => (
            <button
              key={`${chip.label}-${i}`}
              type="button"
              onClick={chip.onClick}
              style={{
                height: 24,
                padding: "0 9px",
                fontSize: 11.5,
                borderRadius: 999,
                fontFamily: "var(--font-sans)",
                background: "var(--card)",
                color: "var(--ink-2)",
                border: "1px solid var(--border)",
                cursor: chip.onClick ? "pointer" : "default",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResponseCardV3;
